import { Box, Flex, createStylesContext, useColorMode } from '@chakra-ui/react'
import {
  createContext,
  useContext,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import {
  ClearIndicatorProps,
  ContainerProps,
  ControlProps,
  DropdownIndicatorProps,
  IndicatorsContainerProps,
  MenuListProps,
  MenuProps,
  OptionProps,
  PlaceholderProps,
  SingleValueProps,
  ValueContainerProps,
} from 'react-select'

import { HighlightText } from '../../HighlightText/HighlightText'
import { CheckIcon } from '../../Icons/CheckIcon'
import { Icon } from '../../Icons/Icon'
import { XIcon } from '../../Icons/XIcon'
import { SelectInputProps } from './SelectInput'

export interface SelectInputOption {
  value: string
  label: string
}

export const rootSelectPropsContext = createContext<SelectInputProps>({})

export const [SelectInputStylesProvider, useSelectInputStyles] =
  createStylesContext('SelectInput')

/**
 * Wrapper for the entire select component. Should not include styling.
 */
export const SelectContainer = ({
  innerProps,
  children,
}: ContainerProps<SelectInputOption, false>) => {
  const styles = useSelectInputStyles()
  const rootProps = useContext(rootSelectPropsContext)

  return (
    <Box
      __css={styles.selectContainer}
      {...innerProps}
      flex={rootProps.flex}
      width={rootProps.width}
      minWidth={rootProps.minWidth}
      mb={rootProps.mb}
      marginRight={rootProps.marginRight}
      data-testid={rootProps['data-testid']}
      data-part="SelectContainer"
    >
      {children}
    </Box>
  )
}

export const Control = ({
  innerRef,
  innerProps,
  menuIsOpen,
  isFocused,
  isDisabled,
  selectProps,
  children, // ...props
}: ControlProps<SelectInputOption, false>) => {
  const styles = useSelectInputStyles() as any

  return (
    <Flex
      data-part="Control"
      data-menu-open={menuIsOpen ? 'true' : undefined}
      ref={innerRef}
      __css={{
        ...styles.control,
        ...(menuIsOpen || isFocused ? styles.control._focus : {}),
        ...(isDisabled ? styles.control._disabled : {}),
      }}
      {...innerProps}
    >
      {children}
    </Flex>
  )
}

const DROPDOWN_DARK_BG = '#232526'
/** Shared dark option hover bg so SelectInput and PickList stay in sync */
export const DROPDOWN_DARK_OPTION_HOVER_BG = '#1D272B'
/** Same gray as option border-bottom in dark mode */
const DROPDOWN_DARK_BORDER = '1px solid #515151'

export const Option = ({
  children,
  innerRef,
  innerProps,
  isSelected,
  isFocused,
  data,
  selectProps,
}: OptionProps<SelectInputOption, false>) => {
  const { colorMode } = useColorMode()
  const styles = useSelectInputStyles() as any
  const { filterMode } = useContext(rootSelectPropsContext)
  const searchQuery = selectProps?.inputValue || ''
  const { style: _reactSelectOptionStyle, ...restInnerProps } = (innerProps ??
    {}) as Record<string, unknown>

  const darkModeOptionStyle =
    colorMode === 'dark'
      ? {
          backgroundColor: isFocused
            ? DROPDOWN_DARK_OPTION_HOVER_BG
            : DROPDOWN_DARK_BG,
        }
      : undefined

  return (
    <Box
      ref={innerRef}
      {...restInnerProps}
      style={darkModeOptionStyle}
      __css={{
        ...styles.option,
        ...(data.value === '' ? styles.option._placeholder : {}),
        ...(isSelected ? styles.option._selected : {}),
        ...(isFocused ? styles.option._hover : {}),
      }}
      data-part="Option"
      data-testid="select-input-option"
    >
      <Box flex="1" minWidth={0}>
        <HighlightText
          text={data.label}
          searchTerm={searchQuery}
          matchMode={filterMode === 'substring' ? 'substring' : 'subsequence'}
        />
      </Box>
      {isSelected && <CheckIcon color="presets.turquoise.60" flexShrink={0} />}
    </Box>
  )
}

export const Menu = (props: MenuProps<SelectInputOption, false>) => {
  const { colorMode } = useColorMode()
  const styles = useSelectInputStyles()
  const rootProps = useContext(rootSelectPropsContext)
  const menuBoundaryId = rootProps.menuBoundaryId

  const menuStyle = props.getStyles('menu', props) as React.CSSProperties
  const { position, top, bottom, width } = menuStyle

  return (
    <Box
      ref={props.innerRef}
      __css={styles.menu}
      style={{
        position,
        top,
        bottom,
        width,
        ...(colorMode === 'dark' && {
          backgroundColor: DROPDOWN_DARK_BG,
          border: DROPDOWN_DARK_BORDER,
        }),
      }}
      data-part="menu"
      data-testid="select-input-menu"
      {...props.innerProps}
      {...(menuBoundaryId && { 'data-click-boundary': menuBoundaryId })}
    >
      {props.children}
    </Box>
  )
}

export const MenuList = ({
  children,
  innerRef,
  innerProps,
}: MenuListProps<SelectInputOption, false>) => {
  const { colorMode } = useColorMode()
  const styles = useSelectInputStyles() as any
  const rootProps = useContext(rootSelectPropsContext)
  const menuListRef = useRef<HTMLDivElement>(null)
  const [maxHeight, setMaxHeight] = useState<number | undefined>(undefined)

  useLayoutEffect(() => {
    if (!rootProps.maxVisibleOptions || !menuListRef.current) {
      setMaxHeight(undefined)
      return
    }

    // Use requestAnimationFrame to ensure DOM is fully rendered
    requestAnimationFrame(() => {
      const firstOption = menuListRef.current?.querySelector(
        '[data-testid="select-input-option"]',
      ) as HTMLElement

      if (firstOption && rootProps.maxVisibleOptions) {
        const optionHeight = firstOption.offsetHeight
        setMaxHeight(optionHeight * rootProps.maxVisibleOptions)
      }
    })
  }, [rootProps.maxVisibleOptions, children])

  return (
    <Box
      ref={node => {
        if (node) {
          ;(
            menuListRef as React.MutableRefObject<HTMLDivElement | null>
          ).current = node
        }
        if (typeof innerRef === 'function') {
          innerRef(node)
        } else if (innerRef && node) {
          ;(innerRef as React.MutableRefObject<HTMLDivElement | null>).current =
            node
        }
      }}
      {...innerProps}
      __css={styles.menuList}
      style={
        colorMode === 'dark'
          ? {
              backgroundColor: DROPDOWN_DARK_BG,
              border: DROPDOWN_DARK_BORDER,
            }
          : undefined
      }
      data-part="menuList"
      {...(maxHeight !== undefined && { maxHeight })}
    >
      {children}
    </Box>
  )
}

export const ValueContainer = ({
  children,
  innerProps,
}: ValueContainerProps<SelectInputOption, false>) => {
  const styles = useSelectInputStyles()
  return (
    <Box
      __css={styles.valueContainer}
      {...innerProps}
      data-part="ValueContainer"
    >
      {children}
    </Box>
  )
}

export const IndicatorSeparator = () => null

export const IndicatorsContainer = ({
  children,
  innerProps,
}: IndicatorsContainerProps<SelectInputOption, false>) => {
  const styles = useSelectInputStyles()
  return (
    <Box
      __css={styles.indicatorsContainer}
      {...innerProps}
      data-part="IndicatorsContainer"
    >
      {children}
    </Box>
  )
}

export const Placeholder = ({
  children,
  innerProps,
}: PlaceholderProps<SelectInputOption, false>) => {
  const styles = useSelectInputStyles() as any

  return (
    <Box
      __css={styles.control._placeholder}
      gridArea="1/1/2/3"
      {...innerProps}
      data-part="Placeholder"
    >
      {children}
    </Box>
  )
}
export const SingleValue = ({
  children,
  innerProps,
}: SingleValueProps<SelectInputOption, false>) => {
  const styles = useSelectInputStyles()
  return (
    <Box
      __css={styles.singleValue}
      gridArea="1/1/2/3"
      {...innerProps}
      data-part="SingleValue"
      data-testid="select-input-single-value"
    >
      {children}
    </Box>
  )
}

export const DropdownIndicator = ({
  selectProps,
}: DropdownIndicatorProps<SelectInputOption, false>) => {
  const styles = useSelectInputStyles()

  return (
    <Icon
      icon="ChevronIcon"
      cursor="pointer"
      __css={styles.dropdownIndicator}
      transform={selectProps.menuIsOpen ? 'rotate(180deg)' : 'rotate(0deg)'}
      data-part="DropdownIndicator"
    />
  )
}

export const ClearIndicator = ({
  innerProps,
}: ClearIndicatorProps<SelectInputOption, false>) => (
  <Flex {...innerProps} data-part="ClearIndicator">
    <XIcon
      backgroundColor="presets.pink.70"
      color="presets.neutral.0"
      rounded="full"
      padding="2px"
      boxSize="14px"
      mr="4px"
    />
  </Flex>
)
